/**
 * Microsoft Graph API utility — Express API Proxy
 *
 * All MS Graph calls are routed through the Express backend at /api/*.
 * MS credentials (MS_TENANT_ID, MS_CLIENT_ID, MS_CLIENT_SECRET) stay server-side only.
 * In dev, Vite proxies /api/* → http://localhost:3001.
 * In production, the same Express server serves both static files and /api/*.
 */

const API_BASE = '/api';

export const BERATER = [
  {
    id: 'rainer',
    name: 'Rainer Gottula',
    email: import.meta.env?.VITE_BERATER_1_EMAIL || 'gottula@zba.academy',
  },
  {
    id: 'charline',
    name: 'Charline Klatt',
    email: import.meta.env?.VITE_BERATER_3_EMAIL || 'c.klatt@zba.academy',
  },
];

/** Returns true — backend is always available via /api */
export function isMSConfigured() {
  return true;
}

/** Mock slots used for UI fallback display */
function getMockSlots() {
  return {
    morning: [
      { time: '08:00', berater: BERATER[0]?.name, beraterEmail: BERATER[0]?.email },
      { time: '09:00', berater: BERATER[1]?.name, beraterEmail: BERATER[1]?.email },
      { time: '10:00', berater: BERATER[0]?.name, beraterEmail: BERATER[0]?.email },
      { time: '11:00', berater: BERATER[1]?.name, beraterEmail: BERATER[1]?.email },
    ],
    afternoon: [
      { time: '13:00', berater: BERATER[0]?.name, beraterEmail: BERATER[0]?.email },
      { time: '14:00', berater: BERATER[1]?.name, beraterEmail: BERATER[1]?.email },
      { time: '15:00', berater: BERATER[0]?.name, beraterEmail: BERATER[0]?.email },
    ],
    isReal: false,
  };
}

function parseSlots(slots) {
  return slots.map(s => ({
    time: s?.time,
    berater: s?.berater?.name || s?.berater,
    beraterEmail: s?.berater?.email || s?.beraterEmail,
  }));
}

/**
 * Fetch available slots for a given date.
 * Returns { morning, afternoon, isReal, mock, reason, error, latency }
 */
function localDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export async function fetchAvailableSlots(date, consultationType, source) {
  const dateISO = date instanceof Date ? localDateStr(date) : date;
  const type = consultationType || 'phone';

  try {
    const t0 = performance.now();
    const res = await fetch(`${API_BASE}/slots?date=${dateISO}&type=${type}&source=${source || 'regular'}`);
    const latency = Math.round(performance.now() - t0);

    if (!res.ok) {
      throw new Error(`Server responded with ${res.status}`);
    }

    const data = await res.json();
    const slots = data?.slots || [];

    const morning = parseSlots(slots.filter(s => {
      const h = parseInt(s?.time?.split(':')[0], 10);
      return h < 12;
    }));
    const afternoon = parseSlots(slots.filter(s => {
      const h = parseInt(s?.time?.split(':')[0], 10);
      return h >= 12;
    }));

    if (data?.mock) {
      return { morning, afternoon, isReal: false, mock: true, reason: data?.reason || 'server_mock', latency };
    }

    return { morning, afternoon, isReal: true, mock: false, latency };
  } catch (err) {
    console.warn('[graphApi] fetchAvailableSlots error, falling back to mock:', err?.message);
    return { ...getMockSlots(), mock: true, reason: 'graph_error', error: err?.message };
  }
}

/**
 * Create a calendar event via Express API.
 */
export async function createBookingEvent(bookingData) {
  try {
    const t0 = performance.now();
    const res = await fetch(`${API_BASE}/create-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    const latency = Math.round(performance.now() - t0);

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData?.error || `Server responded with ${res.status}`);
    }

    const data = await res.json();
    return { ...data, latency };
  } catch (err) {
    console.error('[graphApi] createBookingEvent error:', err?.message);
    return { success: false, mock: true, error: err?.message };
  }
}

/**
 * Check if a specific slot is still available.
 */
export async function checkSlotStillAvailable(date, time, duration, beraterEmail) {
  try {
    const res = await fetch(`${API_BASE}/check-slot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, time, duration, beraterEmail }),
    });

    if (!res.ok) return true;
    const data = await res.json();
    return data?.available !== false;
  } catch (err) {
    console.warn('[graphApi] checkSlotStillAvailable error:', err?.message);
    return true;
  }
}

/**
 * Fetch free/busy blocks for both beraters.
 */
export async function fetchFreeBusy(startDate, endDate) {
  try {
    const res = await fetch(`${API_BASE}/free-busy?startDate=${startDate}&endDate=${endDate}`);
    if (!res.ok) throw new Error(`Server responded with ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('[graphApi] fetchFreeBusy error:', err?.message);
    return { mock: true, error: err?.message };
  }
}

/**
 * Get Graph API connection status via /api/health?graph=true.
 * Returns { connected, latencyMs, lastRefresh, tenantId, error, reason, debug }
 */
export async function getGraphStatus() {
  try {
    const t0 = performance.now();
    const res = await fetch(`${API_BASE}/health?graph=true`);
    const latency = Math.round(performance.now() - t0);

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`Health check failed: HTTP ${res.status} — ${errText?.substring(0, 200)}`);
    }

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const raw = await res.text().catch(() => '');
      throw new Error(`Backend nicht erreichbar (kein JSON). Antwort: ${raw?.substring(0, 120)}`);
    }

    const data = await res.json();
    const graph = data?.graph || {};

    return {
      connected: graph?.connected === true,
      latencyMs: graph?.latency ?? latency,
      lastRefresh: graph?.lastTokenRefresh || null,
      tenantId: graph?.tenantId || null,
      error: graph?.error || null,
      reason: graph?.reason || null,
      debug: graph?.debug || null,
    };
  } catch (err) {
    console.error('[graphApi] getGraphStatus error:', err?.message);
    return { connected: false, latencyMs: 0, lastRefresh: null, error: err?.message, reason: 'fetch_failed', debug: { apiBase: API_BASE } };
  }
}
